/**
 * Editable embroidery piece quantity on cart / cart drawer.
 * Converts pieces to Shopify line qty (cents mode) and syncs line properties.
 */
(function () {
  if (customElements.get("embroidery-cart-qty")) return;

  const DEBOUNCE_MS = 400;

  function parseMoney(value) {
    if (value == null || value === "") return NaN;
    const match = String(value)
      .replace(/,/g, "")
      .match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : NaN;
  }

  function formatMoney(amount) {
    const n = Number(amount);
    if (!isFinite(n)) return "—";
    return "$" + n.toFixed(2) + " USD";
  }

  function debounce(fn, wait) {
    let timer;
    return function debounced() {
      const args = arguments;
      const ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function parseVolumeDiscountTiers(raw) {
    if (!raw) return [];
    try {
      const tiers = JSON.parse(raw);
      if (!Array.isArray(tiers)) return [];
      return tiers
        .map(function (tier) {
          return {
            min: parseInt(tier.min, 10) || 0,
            percent: parseFloat(tier.percent) || 0,
          };
        })
        .filter(function (tier) {
          return tier.min > 0 && tier.percent > 0;
        })
        .sort(function (a, b) {
          return b.min - a.min;
        });
    } catch (e) {
      return [];
    }
  }

  function getVolumeDiscountPercent(pieces, tiers, unitPrice, minOrder) {
    if (!isFinite(unitPrice) || unitPrice <= 0) return 0;
    const subtotal = unitPrice * pieces;
    if (minOrder > 0 && subtotal < minOrder) return 0;
    for (let i = 0; i < tiers.length; i++) {
      if (pieces >= tiers[i].min) return tiers[i].percent;
    }
    return 0;
  }

  function lineTotalFor(pieces, unitPrice, tiers, minOrder) {
    const subtotal = unitPrice * pieces;
    const discountPercent = getVolumeDiscountPercent(pieces, tiers, unitPrice, minOrder);
    return subtotal * (1 - discountPercent / 100);
  }

  class EmbroideryCartQty extends HTMLElement {
    connectedCallback() {
      this.pcsInput = this.querySelector("[data-embroidery-pcs]");
      this.shopifyQtyInput = this.querySelector("[data-embroidery-shopify-qty]");
      this.propsEl = this.querySelector("[data-embroidery-props]");
      this.minPcs = parseInt(this.dataset.minPcs || "10", 10) || 10;
      this.minOrder = parseFloat(this.dataset.minOrder || "0") || 0;
      this.volumeDiscountTiers = parseVolumeDiscountTiers(this.dataset.volumeDiscounts);
      this.pricingMode = (this.dataset.pricingMode || "cents").toLowerCase();
      this.line = this.dataset.line;
      this.variantId = this.dataset.variantId;

      if (!this.pcsInput || !this.shopifyQtyInput || !this.line) return;

      this._onButtonClick = this.onButtonClick.bind(this);
      this._onPcsChange = debounce(this.onPcsChange.bind(this), DEBOUNCE_MS);
      this._stopBubble = this.stopBubble.bind(this);

      this.querySelectorAll(".quantity__button").forEach((button) => {
        button.addEventListener("click", this._onButtonClick);
      });
      this.pcsInput.addEventListener("change", this._onPcsChange);
      this.pcsInput.addEventListener("change", this._stopBubble);
      this.pcsInput.addEventListener("input", this._stopBubble);
      this.validateButtons();
    }

    disconnectedCallback() {
      this.querySelectorAll(".quantity__button").forEach((button) => {
        button.removeEventListener("click", this._onButtonClick);
      });
      if (this.pcsInput) {
        this.pcsInput.removeEventListener("change", this._onPcsChange);
        this.pcsInput.removeEventListener("change", this._stopBubble);
        this.pcsInput.removeEventListener("input", this._stopBubble);
      }
    }

    stopBubble(event) {
      event.stopPropagation();
    }

    readProperties() {
      if (!this.propsEl) return {};
      try {
        return JSON.parse(this.propsEl.textContent || "{}") || {};
      } catch (e) {
        return {};
      }
    }

    getUnitPrice(properties) {
      const fromProp = parseMoney(properties["Price each"]);
      if (isFinite(fromProp) && fromProp > 0) return fromProp;

      const rate = parseFloat(properties["_Rate per sq in"]);
      const sqin = parseFloat(properties["_Square inches"]);
      if (isFinite(rate) && isFinite(sqin) && rate > 0 && sqin > 0) {
        return rate * sqin;
      }
      return NaN;
    }

    normalizePieces(raw) {
      let pieces = parseInt(raw, 10);
      if (!isFinite(pieces) || pieces < this.minPcs) pieces = this.minPcs;
      return pieces;
    }

    shopifyQuantityFor(pieces, unitPrice) {
      if (this.pricingMode === "function") {
        return Math.max(1, pieces);
      }
      if (!isFinite(unitPrice) || unitPrice <= 0) return null;
      const total = lineTotalFor(pieces, unitPrice, this.volumeDiscountTiers, this.minOrder);
      return Math.max(1, Math.round(total * 100));
    }

    buildUpdatedProperties(properties, pieces, unitPrice) {
      const next = Object.assign({}, properties);
      const discountPercent = getVolumeDiscountPercent(
        pieces,
        this.volumeDiscountTiers,
        unitPrice,
        this.minOrder
      );
      const subtotal = unitPrice * pieces;
      const lineTotal = subtotal * (1 - discountPercent / 100);
      next["_Pieces"] = String(pieces);
      next["Quantity (pcs)"] = String(pieces) + " pcs";
      if (isFinite(unitPrice) && unitPrice > 0) {
        next["Price each"] = formatMoney(unitPrice);
        next["_Line total"] = formatMoney(lineTotal);
        next["Volume discount"] = discountPercent > 0 ? discountPercent + "%" : "";
      }
      return next;
    }

    validateButtons() {
      const value = this.normalizePieces(this.pcsInput.value);
      const minus = this.querySelector(".quantity__button[name='minus']");
      if (minus) minus.classList.toggle("disabled", value <= this.minPcs);
    }

    onButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const button = event.currentTarget;
      if (button.classList.contains("disabled")) return;

      const previous = this.normalizePieces(this.pcsInput.value);
      let next = previous;
      if (button.name === "plus") next = previous + 1;
      else if (button.name === "minus") next = Math.max(this.minPcs, previous - 1);

      if (next === previous) return;
      this.pcsInput.value = String(next);
      this.validateButtons();
      this.applyPieces(next);
    }

    onPcsChange() {
      const pieces = this.normalizePieces(this.pcsInput.value);
      this.pcsInput.value = String(pieces);
      this.validateButtons();
      this.applyPieces(pieces);
    }

    applyPieces(pieces) {
      const properties = this.readProperties();
      const unitPrice = this.getUnitPrice(properties);
      const shopifyQty = this.shopifyQuantityFor(pieces, unitPrice);

      if (shopifyQty == null) {
        console.warn("[embroidery-cart-qty] Missing unit price; cannot update line.");
        return;
      }

      const currentShopifyQty = parseInt(this.shopifyQtyInput.value, 10);
      const currentPieces = parseInt(properties["_Pieces"] || this.pcsInput.defaultValue || "0", 10);
      if (shopifyQty === currentShopifyQty && pieces === currentPieces) return;

      const updatedProperties = this.buildUpdatedProperties(properties, pieces, unitPrice);
      this.shopifyQtyInput.value = String(shopifyQty);
      this.shopifyQtyInput.setAttribute("value", String(shopifyQty));

      const cartItems = this.closest("cart-items") || this.closest("cart-drawer-items");
      if (!cartItems || typeof cartItems.updateQuantity !== "function") {
        console.warn("[embroidery-cart-qty] cart-items not found");
        return;
      }

      cartItems.updateQuantity(this.line, shopifyQty, "plus", this.variantId, updatedProperties);
    }
  }

  customElements.define("embroidery-cart-qty", EmbroideryCartQty);
})();
